import type { Article } from "./types";

export const orgtoolsVsQuantive: Article = {
  slug: "orgtools-vs-quantive",
  kind: "comparison",
  title: "Orgtools vs Quantive: Decision Record vs OKR Platform",
  description:
    "Quantive tracks OKRs and goal progress. Orgtools records the decisions that set and change those goals, and whether the outcomes matched. How they compare and when to use each.",
  lede: "Quantive is an OKR and strategy execution platform. Orgtools is not an OKR tool. OKRs run on a quarterly rhythm; Orgtools keeps up with the calls your people and agents make between check-ins, and puts the ones that matter in front of you.",
  datePublished: "2026-06-26",
  sections: [
    {
      type: "prose",
      heading: "What Sets Orgtools Apart from Quantive",
      body: "Quantive is built around objectives and key results: setting goals, cascading them across teams, and tracking progress, increasingly with AI that translates strategy into OKRs and flags misalignment. It answers how the company is progressing against its goals.\n\nOrgtools answers a different question: what is being decided between check-ins. When people and agents do a week's work in a day, the calls that move a goal happen inside sessions and threads long before the next review. Orgtools finds the decisions a plan depends on, shows who decided what wherever they decided it, and runs the calls that need a leader with a named owner and the reasoning on the record. OKRs measure goal progress. Orgtools shows the calls behind it as they are made.",
    },
    {
      type: "table",
      heading: "Head-to-Head Comparison",
      columns: ["Capability", "Orgtools", "Quantive"],
      rows: [
        ["Structured decision record", "Yes", "OKRs and goals, not decisions"],
        ["Captures reasoning behind a goal", "Yes", "Goal definitions and KPIs"],
        ["Named accountability for the call", "Yes", "Goal owners"],
        ["Outcome tracked against the decision", "Yes", "OKR progress against targets"],
        ["Drift, drag, and decay signals", "Yes", "Goal metrics only"],
        ["OKR cascading and KPI tracking", "Not the focus", "Core strength"],
      ],
    },
    {
      type: "prose",
      heading: "When to Choose Quantive",
      body: "Choose Quantive when the core need is OKR management: defining objectives, cascading them across a large organization, and tracking key results against targets, with integrations that update progress from business data. If you have a strong decision-making practice already and need a mature OKR engine, that is what Quantive is for.",
    },
    {
      type: "prose",
      heading: "When to Choose Orgtools",
      body: "Choose Orgtools when the work moves faster than the review cycle: your people and agents make calls every day, and the quarterly check-in finds out weeks later. OKRs tell you whether you hit the number. Orgtools shows the calls behind the number as they are made, lets you step in on the ones that matter, and keeps the reasoning when people leave. The two run side by side.",
    },
    {
      type: "cta",
      heading: "See What Gets Decided Between Check-Ins",
      href: "/platform",
      label: "Explore the Platform",
    },
  ],
  faq: [
    {
      question: "Is Orgtools an OKR tool?",
      answer:
        "No. Orgtools works alongside your OKRs. It finds the decisions a plan depends on and shows what your people and agents decide between check-ins, with the reasoning behind each. Many teams use both: the goal progress in an OKR platform, the decisions in Orgtools.",
    },
    {
      question: "How is a decision record different from an OKR?",
      answer:
        "An OKR is a target and its measures. A decision record is the choice that set that target: the reasoning, the alternatives, the data, the owner, and the outcome against intent. One measures progress. The other explains and accounts for the call.",
    },
    {
      question: "Can Orgtools and Quantive be used together?",
      answer:
        "Yes. They address different layers. Orgtools holds the decisions and their reasoning. An OKR platform tracks progress against the goals those decisions set.",
    },
  ],
};
